/**
 * Tariff Classifier Page
 * Main page for HS code classification.
 *
 * Tabs: Direct → Upload → Excel Add-in → History
 * The Upload tab unifies what were previously File Upload, Image Classification,
 * and Bulk Upload tabs. CSV/Excel files route through the bulk-upload endpoint
 * (which prompts for a column mapping only when auto-detection fails);
 * PDFs and images are classified directly with an optional per-file description.
 */
import React, { useState, useEffect, useCallback } from 'react';
import {
  Box,
  Container,
  Typography,
  Tabs,
  Tab,
  Paper,
  Divider,
  Button,
  Alert,
  Breadcrumbs,
  Link,
  Chip,
  LinearProgress,
  IconButton,
  Menu,
  MenuItem,
} from '@mui/material';
import {
  LocalShipping as TariffIcon,
  FileUpload as UploadIcon,
  History as HistoryIcon,
  Home as HomeIcon,
  Search as SearchIcon,
  CheckCircle as SuccessIcon,
  ContentCopy as CopyIcon,
  TableView as ExcelIcon,
} from '@mui/icons-material';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { toast } from 'react-toastify';

import { brand } from '../../../theme';
import UnifiedUploadZone, { QueuedFile } from '../components/UnifiedUploadZone';
import ColumnMappingModal from '../components/ColumnMappingModal';
import ResultsTable from '../components/ResultsTable';
import DirectClassificationForm from '../components/DirectClassificationForm';
import TransactionHistory from '../components/TransactionHistory';
import TariffTransactionDetailModal from '../components/TariffTransactionDetailModal';
import ExcelAddinTab from '../components/ExcelAddinTab';
import ClassificationModeToggle from '../components/ClassificationModeToggle';

import {
  confirmColumnMapping,
  getMappingPreview,
  uploadFilesBulk,
  classifyBatch,
  classifyDirect,
  getBatchProgress,
  downloadResults,
  downloadBulkResults,
  downloadBulkMerged,
  getHistory,
  triggerDownload,
  ClassificationResult,
  TransactionHistory as Transaction,
  BulkUploadResponse,
  BulkFileInfo,
  FilePreviewResponse,
} from '../../../services/tariffClassifierPageService';
import { useTranslation } from '../../../store/languageStore';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

const TabPanel: React.FC<TabPanelProps> = ({ children, value, index }) => {
  return (
    <div role="tabpanel" hidden={value !== index}>
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
};

// Tab order: Direct (0) → Upload (1) → Excel Add-in (2) → History (3).
// Lets other pages deep-link to a tab via ?tab=history.
const TAB_INDEX_BY_NAME: Record<string, number> = { direct: 0, upload: 1, excel: 2, history: 3 };

const TariffClassifierPage: React.FC = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { t, currentLanguage } = useTranslation();
  const [activeTab, setActiveTab] = useState(() => TAB_INDEX_BY_NAME[searchParams.get('tab') || ''] ?? 0);

  // Upload / classification state
  const [uploadLoading, setUploadLoading] = useState(false);
  const [uploadError, setUploadError] = useState<string | null>(null);
  const [currentTransactionId, setCurrentTransactionId] = useState<string | null>(null);
  const [classificationResults, setClassificationResults] = useState<ClassificationResult[]>([]);
  const [processLoading, setProcessLoading] = useState(false);
  const [downloadLoading, setDownloadLoading] = useState(false);

  // Direct (text) classification state
  const [directClassifyLoading, setDirectClassifyLoading] = useState(false);

  // Unstructured (PDF / image) results — rendered as cards below the dropzone
  const [documentResults, setDocumentResults] = useState<ClassificationResult[]>([]);

  // History state
  const [history, setHistory] = useState<Transaction[]>([]);
  const [historyLoading, setHistoryLoading] = useState(false);

  // Detail modal state
  const [detailModalOpen, setDetailModalOpen] = useState(false);
  const [selectedTransactionId, setSelectedTransactionId] = useState<string | null>(null);

  // Bulk upload state
  const [bulkUploadResult, setBulkUploadResult] = useState<BulkUploadResponse | null>(null);
  const [bulkClassified, setBulkClassified] = useState(false);
  // Actual number of files that successfully classified. Backend `successful_files`
  // only reflects upload success, not classification success.
  const [bulkSuccessCount, setBulkSuccessCount] = useState(0);
  // Transaction IDs whose description column needs user input before classification
  const [bulkMappingQueue, setBulkMappingQueue] = useState<string[]>([]);
  // When true, the auto-classify effect fires once the mapping queue drains
  const [autoClassifyAfterMapping, setAutoClassifyAfterMapping] = useState(false);

  // Column mapping modal state. mappingContext routes the modal's onConfirm:
  // - 'bulk'    : mid-upload mapping queue (current run)
  // - 'history' : resuming an old awaiting_mapping transaction from History
  const [columnMappingOpen, setColumnMappingOpen] = useState(false);
  const [filePreviewData, setFilePreviewData] = useState<FilePreviewResponse | null>(null);
  const [mappingLoading, setMappingLoading] = useState(false);
  const [mappingContext, setMappingContext] = useState<'bulk' | 'history'>('bulk');
  // Transaction whose mapping preview is currently being fetched (for the spinner on the History row's ▶ button)
  const [resumingMappingId, setResumingMappingId] = useState<string | null>(null);

  // Shared classification mode across tabs
  const [classificationMode, setClassificationMode] = useState<'quick' | 'advanced'>(
    'quick'
  );

  // Anchor for the "Download merged" format-picker menu in the bulk results panel.
  const [mergedMenuAnchor, setMergedMenuAnchor] = useState<HTMLElement | null>(null);

  // Per-file bulk progress (current/total + items processed within the current file)
  const [bulkProgress, setBulkProgress] = useState<{
    current: number;
    total: number;
    filename: string;
    processed?: number;
    total_items?: number;
  } | null>(null);

  const loadHistory = async () => {
    setHistoryLoading(true);
    try {
      const data = await getHistory();
      setHistory(data);
    } catch (error) {
      console.error('Failed to load history:', error);
    } finally {
      setHistoryLoading(false);
    }
  };

  // Classify every uploaded tabular file in the most recent bulk upload.
  // Declared before the effect below so the dependency array doesn't read it in
  // its temporal dead zone (this previously crashed the page on mount).
  const autoClassifyBulk = useCallback(
    async (result: BulkUploadResponse) => {
      setProcessLoading(true);
      setBulkProgress(null);
      let successCount = 0;
      // Only run classify-batch on tabular files that finished extraction.
      // Unstructured (PDF/image) files in the same batch are already `completed`
      // with output_data — running classifyBatch on them would clobber results.
      const filesToProcess = result.file_details.filter(
        (fd) => fd.transaction_id && fd.status === 'pending'
      );
      const alreadyCompletedCount = result.file_details.filter(
        (fd) => fd.status === 'completed'
      ).length;
      const totalFiles = filesToProcess.length;
      // For the single-file case, capture the response so we can populate the
      // inline ResultsTable. Multi-file runs only show the bulk summary.
      let lastResponseResults: ClassificationResult[] | null = null;
      let lastResponseTransactionId: string | null = null;

      try {
        for (let i = 0; i < filesToProcess.length; i++) {
          const fileDetail = filesToProcess[i];
          setBulkProgress({
            current: i + 1,
            total: totalFiles,
            filename: fileDetail.filename,
            processed: 0,
            total_items: fileDetail.items_extracted || 0,
          });

          const progressInterval = setInterval(async () => {
            try {
              const progress = await getBatchProgress(fileDetail.transaction_id);
              if (progress) {
                setBulkProgress((prev) =>
                  prev
                    ? { ...prev, processed: progress.processed, total_items: progress.total }
                    : prev
                );
              }
            } catch (err) {
              /* progress polling is best-effort */
            }
          }, 1500);

          try {
            const response = await classifyBatch(
              fileDetail.transaction_id,
              classificationMode
            );
            successCount++;
            lastResponseResults = response.results;
            lastResponseTransactionId = fileDetail.transaction_id;
          } catch (err) {
            console.error(`Failed to classify ${fileDetail.filename}:`, err);
          } finally {
            clearInterval(progressInterval);
          }
        }

        // Single-file case: surface the per-row results inline
        if (totalFiles === 1 && lastResponseResults) {
          setClassificationResults(lastResponseResults);
          setCurrentTransactionId(lastResponseTransactionId);
        }

        const modeLabel =
          classificationMode === 'quick' ? '(Quick Mode)' : '(Advanced Mode)';
        const totalClassified = successCount + alreadyCompletedCount;
        toast.success(`Classified ${totalClassified} file(s) ${modeLabel}!`);
        setBulkSuccessCount(totalClassified);
        setBulkClassified(true);
        loadHistory();
      } finally {
        setProcessLoading(false);
        setBulkProgress(null);
      }
    },
    [classificationMode]
  );

  // Load history on mount
  useEffect(() => {
    loadHistory();
  }, []);

  // When a unified upload requested auto-classify and the bulk mapping queue has
  // drained (and the user isn't mid-modal), classify every uploaded tabular file.
  useEffect(() => {
    if (
      autoClassifyAfterMapping &&
      bulkMappingQueue.length === 0 &&
      bulkUploadResult &&
      !columnMappingOpen
    ) {
      setAutoClassifyAfterMapping(false);
      void autoClassifyBulk(bulkUploadResult);
    }
  }, [
    autoClassifyAfterMapping,
    bulkMappingQueue.length,
    bulkUploadResult,
    columnMappingOpen,
    autoClassifyBulk,
  ]);

  const handleUnifiedProcess = async (queue: QueuedFile[]) => {
    if (queue.length === 0) return;

    // Reset previous-run state so the new run starts clean
    setUploadLoading(true);
    setUploadError(null);
    setClassificationResults([]);
    setCurrentTransactionId(null);
    setDocumentResults([]);
    setBulkUploadResult(null);
    setBulkClassified(false);
    setBulkSuccessCount(0);
    setBulkMappingQueue([]);
    setAutoClassifyAfterMapping(false);

    try {
      // Everything goes through the bulk endpoint: the backend handles CSV/Excel
      // (extract → maybe mapping), PDF/JPG/PNG (synchronous OCR + classify), and
      // stamps every child transaction with bulk_transaction_id for History grouping.
      const items = queue.map((q) => ({
        file: q.file,
        description: q.kind === 'unstructured' ? q.description : undefined,
      }));
      const response = await uploadFilesBulk(items, classificationMode);
      setBulkUploadResult(response);

      // Unstructured (PDF/image) files come back already `completed` with output_data.
      // Pull their results into documentResults so the per-file cards render inline.
      const unstructuredCompleted = response.file_details.filter((fd) => {
        const ext = fd.file_type?.toLowerCase() || '';
        return (
          fd.status === 'completed' &&
          (ext === 'pdf' || ext === 'jpg' || ext === 'jpeg' || ext === 'png')
        );
      });
      if (unstructuredCompleted.length > 0) {
        const mergedResults: ClassificationResult[] = [];
        for (const fd of unstructuredCompleted) {
          if (fd.output_data) mergedResults.push(...fd.output_data);
        }
        setDocumentResults(mergedResults);
        toast.success(
          `Classified ${unstructuredCompleted.length} document/image file(s) (${mergedResults.length} items).`
        );
      }

      // Tabular files: dispatch into the mapping queue or straight into auto-classify
      const needsMapping = response.file_details.filter(
        (fd) => fd.needs_mapping && fd.transaction_id
      );
      const tabularReady = response.file_details.filter(
        (fd) => fd.status === 'pending' && fd.transaction_id
      );

      if (needsMapping.length > 0) {
        setBulkMappingQueue(needsMapping.map((fd) => fd.transaction_id));
        openBulkMapping(needsMapping[0]);
        setAutoClassifyAfterMapping(true);
        toast.info(
          `${needsMapping.length} of ${response.total_files} file(s) need a column mapping.`
        );
      } else if (tabularReady.length > 0) {
        await autoClassifyBulk(response);
      } else if (unstructuredCompleted.length > 0) {
        // Pure unstructured batch — no auto-classify needed; just mark "classified"
        // so the bulk summary panel renders for multi-file runs.
        setBulkSuccessCount(unstructuredCompleted.length);
        setBulkClassified(true);
      }

      loadHistory();
    } catch (error: any) {
      console.error('[TariffClassifier] Unified process error:', error);
      const errorMsg =
        error.response?.data?.detail || error.message || 'Processing failed';
      setUploadError(errorMsg);
      toast.error(errorMsg);
      throw error;
    } finally {
      setUploadLoading(false);
    }
  };

  // Open the mapping modal for one of the bulk-uploaded files awaiting a column choice.
  // The backend returns columns/preview alongside each BulkFileInfo, so we adapt that
  // shape into the FilePreviewResponse the existing modal expects.
  const openBulkMapping = (fileInfo: BulkFileInfo) => {
    setMappingContext('bulk');
    setFilePreviewData({
      transaction_id: fileInfo.transaction_id,
      file_name: fileInfo.filename,
      file_type: fileInfo.file_type,
      columns: fileInfo.columns || [],
      preview_rows: fileInfo.preview_rows || [],
      suggested_description_col: fileInfo.suggested_description_col ?? null,
      suggested_hs_code_col: fileInfo.suggested_hs_code_col ?? null,
      total_rows: fileInfo.total_rows || 0,
      status: fileInfo.status,
    });
    setColumnMappingOpen(true);
  };

  const handleBulkMappingConfirm = async (descriptionColumn: string, hsCodeColumn?: string) => {
    if (!filePreviewData) return;
    const targetId = filePreviewData.transaction_id;

    setMappingLoading(true);
    try {
      const response = await confirmColumnMapping(targetId, descriptionColumn, hsCodeColumn);

      // Mark this file as 'pending' so the auto-classify gate updates,
      // and stamp items_extracted from the backend so the summary stays accurate.
      setBulkUploadResult((prev) =>
        prev
          ? {
              ...prev,
              total_items: prev.total_items + response.total_items,
              file_details: prev.file_details.map((fd) =>
                fd.transaction_id === targetId
                  ? {
                      ...fd,
                      status: 'pending',
                      needs_mapping: false,
                      items_extracted: response.total_items,
                    }
                  : fd
              ),
            }
          : prev
      );

      toast.success(
        `Mapped ${response.file_name || filePreviewData.file_name}: ${response.total_items} items.`
      );

      // Advance the queue
      const remaining = bulkMappingQueue.filter((id) => id !== targetId);
      setBulkMappingQueue(remaining);

      if (remaining.length === 0) {
        setColumnMappingOpen(false);
        setFilePreviewData(null);
      } else {
        const nextFile = bulkUploadResult?.file_details.find(
          (fd) => fd.transaction_id === remaining[0]
        );
        if (nextFile) {
          openBulkMapping(nextFile);
        } else {
          setColumnMappingOpen(false);
          setFilePreviewData(null);
        }
      }
    } catch (error: any) {
      const errorMsg = error.response?.data?.detail || 'Column mapping failed';
      toast.error(errorMsg);
    } finally {
      setMappingLoading(false);
    }
  };

  // Resume an awaiting_mapping transaction from the History tab. Re-fetches the
  // column preview from the saved file, opens the modal, and on confirm runs
  // confirmColumnMapping + classifyBatch for just that one transaction.
  const handleResumeMapping = async (transactionId: string) => {
    setResumingMappingId(transactionId);
    try {
      const preview = await getMappingPreview(transactionId);
      // Clear stale bulk-panel state from any earlier in-page upload so the
      // user isn't confused by "N of M files classified" stats next to a
      // freshly-resumed History flow.
      setBulkUploadResult(null);
      setBulkClassified(false);
      setBulkSuccessCount(0);
      setMappingContext('history');
      setFilePreviewData(preview);
      setColumnMappingOpen(true);
    } catch (err: any) {
      const errorMsg = err.response?.data?.detail || 'Could not load mapping preview';
      toast.error(errorMsg);
    } finally {
      setResumingMappingId(null);
    }
  };

  const handleResumeMappingConfirm = async (
    descriptionColumn: string,
    hsCodeColumn?: string
  ) => {
    if (!filePreviewData) return;
    const targetId = filePreviewData.transaction_id;

    // Phase 1: confirm column mapping (modal stays open with its own loading state)
    setMappingLoading(true);
    let totalItems = 0;
    try {
      const mapResp = await confirmColumnMapping(targetId, descriptionColumn, hsCodeColumn);
      totalItems = mapResp.total_items;
      setColumnMappingOpen(false);
      setFilePreviewData(null);
    } catch (error: any) {
      toast.error(error.response?.data?.detail || 'Column mapping failed');
      return;
    } finally {
      // Release the modal's loading flag as soon as the modal is gone, so a
      // second resume started from History during the classify phase below
      // doesn't open onto a stuck-loading modal.
      setMappingLoading(false);
    }

    // Phase 2: classify the now-pending file. Modal is already closed; this
    // can take 10-30s and runs in the background.
    try {
      await classifyBatch(targetId, classificationMode);
      toast.success(`Mapped & classified ${totalItems} items.`);
    } catch (err) {
      console.error('[TariffClassifier] Resume classify failed:', err);
      toast.error('Mapping confirmed but classification failed. Try again from History.');
    }
    loadHistory();
  };

  const handleDownload = async (format: 'csv' | 'excel') => {
    if (!currentTransactionId) return;

    setDownloadLoading(true);
    try {
      const blob = await downloadResults(currentTransactionId, format);
      const filename = `tariff_results_${currentTransactionId.substring(0, 8)}.${
        format === 'excel' ? 'xlsx' : 'csv'
      }`;
      triggerDownload(blob, filename);
      toast.success('Download started!');
    } catch (error) {
      toast.error('Download failed');
    } finally {
      setDownloadLoading(false);
    }
  };

  const handleDirectClassify = async (data: { product_description: string }): Promise<any> => {
    setDirectClassifyLoading(true);
    try {
      let response: any;
      if (classificationMode === 'quick') {
        response = await classifyDirect({
          product_description: data.product_description,
          language: currentLanguage,
        });
        toast.success('Classification complete ⚡ (Quick Mode)!');
      } else {
        const { classifyAdvanced } = await import(
          '../../../services/tariffClassifierPageService'
        );
        response = await classifyAdvanced({
          product_description: data.product_description,
          return_alternatives: true,
          language: currentLanguage,
          mode: classificationMode,
        });
        toast.success('Classification complete 🔬 (Advanced Mode)!');
      }
      loadHistory();
      return response;
    } catch (error) {
      throw error;
    } finally {
      setDirectClassifyLoading(false);
    }
  };

  const handleViewDetails = (transactionId: string) => {
    setSelectedTransactionId(transactionId);
    setDetailModalOpen(true);
  };

  const handleDownloadFromHistory = async (transactionId: string) => {
    try {
      const blob = await downloadResults(transactionId, 'csv');
      const filename = `tariff_results_${transactionId.substring(0, 8)}.csv`;
      triggerDownload(blob, filename);
      toast.success('Download started!');
    } catch (error) {
      console.error('[TariffClassifier] Download error:', error);
      toast.error('Download failed');
    }
  };

  const handleDownloadBatchFromHistory = async (transactionIds: string[]) => {
    if (transactionIds.length === 0) {
      toast.warning('No completed transactions in this batch');
      return;
    }
    try {
      const blob = await downloadBulkResults(transactionIds);
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = 'tariff_batch_results.zip';
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success('ZIP downloaded!');
    } catch (error) {
      console.error('[TariffClassifier] Batch download error:', error);
      toast.error('Failed to download ZIP');
    }
  };

  const handleDownloadMergedBatch = async (
    transactionIds: string[],
    format: 'csv' | 'excel'
  ) => {
    if (transactionIds.length === 0) {
      toast.warning('No completed transactions in this batch');
      return;
    }
    try {
      const blob = await downloadBulkMerged(transactionIds, format);
      const ext = format === 'excel' ? 'xlsx' : 'csv';
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `tariff_classifications_merged.${ext}`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Merged ${ext.toUpperCase()} downloaded!`);
    } catch (error) {
      console.error('[TariffClassifier] Merged download error:', error);
      toast.error('Failed to download merged file');
    }
  };

  return (
    <Box sx={{ height: '100%', bgcolor: 'grey.50', overflow: 'auto' }}>
      <Container maxWidth="xl" sx={{ py: 4, px: { xs: 2, sm: 3, md: 4 } }}>
        <Breadcrumbs sx={{ mb: 2 }}>
          <Link
            underline="hover"
            color="inherit"
            sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
            onClick={() => navigate('/process')}
          >
            <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
            Home
          </Link>
          <Typography color="text.primary" sx={{ display: 'flex', alignItems: 'center' }}>
            <TariffIcon sx={{ mr: 0.5 }} fontSize="small" />
            Tariff Classifier
          </Typography>
        </Breadcrumbs>

        <Box sx={{ mb: 4 }}>
          <Typography variant="h4" fontWeight={700} gutterBottom>
            {t('dashboard.cards.tariffClassifier.title')}
          </Typography>
          <Typography variant="body1" color="text.secondary">
            {t('dashboard.cards.tariffClassifier.description')}
          </Typography>
        </Box>

        <Paper elevation={1} sx={{ mb: 3, borderRadius: 2, overflow: 'hidden' }}>
          <Tabs
            value={activeTab}
            onChange={(_e, newValue) => setActiveTab(newValue)}
            sx={{
              borderBottom: 1,
              borderColor: brand.borderSubtle,
              px: 2,
              '& .MuiTabs-indicator': { backgroundColor: brand.orange },
              '& .MuiTab-root': { textTransform: 'none', fontWeight: 600 },
              '& .Mui-selected': { color: `${brand.orange} !important` },
            }}
          >
            <Tab
              icon={<SearchIcon />}
              label={t('dashboard.cards.tariffClassifier.tabs.direct')}
              iconPosition="start"
            />
            <Tab icon={<UploadIcon />} label="Upload" iconPosition="start" />
            <Tab icon={<ExcelIcon />} label="Excel Add-in" iconPosition="start" />
            <Tab
              icon={<HistoryIcon />}
              label={t('dashboard.cards.tariffClassifier.tabs.history')}
              iconPosition="start"
            />
          </Tabs>

          {/* Direct Classification Tab */}
          <TabPanel value={activeTab} index={0}>
            <ClassificationModeToggle
              variant="card"
              value={classificationMode}
              onChange={setClassificationMode}
            />
            <DirectClassificationForm
              onClassify={handleDirectClassify}
              loading={directClassifyLoading}
            />
          </TabPanel>

          {/* Upload Tab — replaces the former Image, File, and Bulk tabs */}
          <TabPanel value={activeTab} index={1}>
            <ClassificationModeToggle
              value={classificationMode}
              onChange={setClassificationMode}
            />

            <UnifiedUploadZone
              onProcess={handleUnifiedProcess}
              processing={uploadLoading || processLoading || mappingLoading}
            />

            {uploadError && (
              <Alert severity="error" sx={{ mt: 2 }} onClose={() => setUploadError(null)}>
                {uploadError}
              </Alert>
            )}

            {/* Bulk mapping warning */}
            {bulkUploadResult && bulkMappingQueue.length > 0 && (
              <Alert severity="warning" sx={{ mt: 3 }}>
                <Typography variant="body2" gutterBottom>
                  <strong>{bulkMappingQueue.length}</strong>
                  {bulkMappingQueue.length === 1 ? ' file needs' : ' files need'} a column
                  mapping before classification can continue.
                </Typography>
                <Button
                  size="small"
                  variant="outlined"
                  color="warning"
                  onClick={() => {
                    const next = bulkUploadResult?.file_details.find(
                      (fd) => fd.transaction_id === bulkMappingQueue[0]
                    );
                    if (next) openBulkMapping(next);
                  }}
                  sx={{ mt: 1 }}
                >
                  Resolve mappings
                </Button>
              </Alert>
            )}

            {/* Bulk per-file + per-item progress (covers single and multi-file uploads) */}
            {bulkProgress && (
              <Box
                sx={{
                  mt: 3,
                  p: 3,
                  border: '1px solid',
                  borderColor: brand.borderSubtle,
                  borderRadius: 2,
                  bgcolor: 'background.paper',
                }}
              >
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Processing file {bulkProgress.current} of {bulkProgress.total}
                  </Typography>
                  <Typography variant="body2" color="primary">
                    {Math.round((bulkProgress.current / bulkProgress.total) * 100)}%
                  </Typography>
                </Box>
                <LinearProgress
                  variant="determinate"
                  value={(bulkProgress.current / bulkProgress.total) * 100}
                  sx={{ height: 8, borderRadius: 4, mb: 1 }}
                />
                {bulkProgress.total_items ? (
                  <Box sx={{ mt: 1.5, pl: 2, borderLeft: '2px solid', borderColor: 'divider' }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 0.5 }}>
                      <Typography variant="caption" color="text.secondary">
                        Items processed in {bulkProgress.filename}:
                      </Typography>
                      <Typography variant="caption" color="secondary" fontWeight={600}>
                        {bulkProgress.processed || 0} / {bulkProgress.total_items}
                      </Typography>
                    </Box>
                    <LinearProgress
                      variant="determinate"
                      color="secondary"
                      value={((bulkProgress.processed || 0) / bulkProgress.total_items) * 100}
                      sx={{ height: 4, borderRadius: 2 }}
                    />
                  </Box>
                ) : (
                  <Typography
                    variant="caption"
                    color="text.secondary"
                    sx={{ mt: 0.5, display: 'block' }}
                  >
                    Current: {bulkProgress.filename}
                  </Typography>
                )}
              </Box>
            )}

            {/* Single-tabular results (CSV/Excel single file) */}
            {classificationResults.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  {t('dashboard.cards.tariffClassifier.classificationResults')}
                </Typography>
                <ResultsTable
                  results={classificationResults}
                  transactionId={currentTransactionId || ''}
                  loading={processLoading}
                  onDownload={handleDownload}
                  downloadLoading={downloadLoading}
                />
              </Box>
            )}

            {/* Bulk results — visible once auto-classify finishes, multi-file only.
                Single-file uploads use the inline ResultsTable above. */}
            {bulkUploadResult && bulkClassified && bulkUploadResult.file_details.length > 1 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Alert
                  severity={
                    bulkSuccessCount === bulkUploadResult.successful_files ? 'success' : 'warning'
                  }
                  sx={{ mb: 2 }}
                >
                  <Typography variant="body2">
                    <strong>{bulkSuccessCount}</strong> of{' '}
                    <strong>{bulkUploadResult.successful_files}</strong> file
                    {bulkUploadResult.successful_files === 1 ? '' : 's'} classified with{' '}
                    <strong>{bulkUploadResult.total_items}</strong> total item
                    {bulkUploadResult.total_items === 1 ? '' : 's'}.
                  </Typography>
                </Alert>
                <Box sx={{ display: 'flex', gap: 2, alignItems: 'center', flexWrap: 'wrap' }}>
                  <Button variant="outlined" onClick={() => setActiveTab(3)}>
                    View in History
                  </Button>
                  <Button
                    variant="outlined"
                    color="primary"
                    disabled={downloadLoading}
                    onClick={(e) => setMergedMenuAnchor(e.currentTarget)}
                  >
                    {downloadLoading ? 'Downloading…' : 'Download ▾'}
                  </Button>
                  <Menu
                    anchorEl={mergedMenuAnchor}
                    open={Boolean(mergedMenuAnchor)}
                    onClose={() => setMergedMenuAnchor(null)}
                  >
                    <MenuItem
                      onClick={async () => {
                        setMergedMenuAnchor(null);
                        if (!bulkUploadResult) return;
                        const ids = bulkUploadResult.file_details
                          .filter((fd) => fd.transaction_id && fd.status === 'completed')
                          .map((fd) => fd.transaction_id);
                        await handleDownloadMergedBatch(ids, 'csv');
                      }}
                    >
                      <Box>
                        <Typography variant="body2">All in one CSV</Typography>
                        <Typography variant="caption" color="text.secondary">
                          One row per item, source_file column
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={async () => {
                        setMergedMenuAnchor(null);
                        if (!bulkUploadResult) return;
                        const ids = bulkUploadResult.file_details
                          .filter((fd) => fd.transaction_id && fd.status === 'completed')
                          .map((fd) => fd.transaction_id);
                        await handleDownloadMergedBatch(ids, 'excel');
                      }}
                    >
                      <Box>
                        <Typography variant="body2">All in one Excel</Typography>
                        <Typography variant="caption" color="text.secondary">
                          Same data as CSV, .xlsx workbook
                        </Typography>
                      </Box>
                    </MenuItem>
                    <MenuItem
                      onClick={async () => {
                        setMergedMenuAnchor(null);
                        if (!bulkUploadResult) return;
                        setDownloadLoading(true);
                        try {
                          const transactionIds = bulkUploadResult.file_details
                            .filter((fd) => fd.transaction_id && fd.status !== 'failed')
                            .map((fd) => fd.transaction_id);
                          if (transactionIds.length === 0) {
                            toast.warning('No completed transactions to download');
                            return;
                          }
                          const blob = await downloadBulkResults(transactionIds);
                          const url = window.URL.createObjectURL(blob);
                          const a = document.createElement('a');
                          a.href = url;
                          a.download = 'bulk_classification_results.zip';
                          a.click();
                          window.URL.revokeObjectURL(url);
                          toast.success('ZIP file downloaded!');
                        } catch (err) {
                          toast.error('Failed to download ZIP');
                        } finally {
                          setDownloadLoading(false);
                        }
                      }}
                    >
                      <Box>
                        <Typography variant="body2">Separate files (ZIP)</Typography>
                        <Typography variant="caption" color="text.secondary">
                          One CSV per uploaded file
                        </Typography>
                      </Box>
                    </MenuItem>
                  </Menu>
                </Box>
              </Box>
            )}

            {/* Unstructured (PDF / image) classification cards */}
            {documentResults.length > 0 && (
              <Box sx={{ mt: 4 }}>
                <Divider sx={{ mb: 3 }} />
                <Typography variant="h6" sx={{ mb: 2 }}>
                  Document / Image Classification Results
                </Typography>
                {documentResults.map((res: ClassificationResult, index: number) => (
                  <Paper
                    key={index}
                    elevation={0}
                    sx={{
                      p: 4,
                      mb: 3,
                      border: '2px solid',
                      borderColor: brand.success,
                      borderRadius: 2,
                    }}
                  >
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5, mb: 2 }}>
                      <SuccessIcon color="success" />
                      <Typography variant="h6" fontWeight={700}>
                        AI Classification Analysis
                      </Typography>
                      <Chip
                        label={`${(res.confidence * 100).toFixed(0)}% Confidence`}
                        color={
                          res.confidence >= 0.9
                            ? 'success'
                            : res.confidence >= 0.7
                            ? 'warning'
                            : 'error'
                        }
                        sx={{ ml: 'auto', fontWeight: 'bold' }}
                      />
                    </Box>
                    <Divider sx={{ mb: 3 }} />

                    <Box sx={{ p: 3, bgcolor: '#e8f5e9', borderRadius: 2, mb: 3 }}>
                      <Typography
                        variant="caption"
                        color="text.secondary"
                        fontWeight={600}
                        sx={{ letterSpacing: 1 }}
                      >
                        GENERATED HS CODE
                      </Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', mt: 1 }}>
                        <Typography
                          variant="h3"
                          fontWeight={800}
                          sx={{ fontFamily: 'monospace', letterSpacing: 2, color: brand.orange }}
                        >
                          {res.generated_hs_code
                            ?.replace(/[\s.-]/g, '')
                            ?.replace(/(.{2})(?=(.{2})+(?!.))/g, '$1.') || 'No Code Found'}
                        </Typography>
                        {res.generated_hs_code && (
                          <IconButton
                            onClick={() => {
                              navigator.clipboard.writeText(res.generated_hs_code || '');
                              toast.success('HS Code copied!');
                            }}
                            sx={{
                              ml: 2,
                              color: brand.orange,
                              bgcolor: 'white',
                              '&:hover': { bgcolor: brand.dropZoneBg },
                            }}
                            size="small"
                            title="Copy HS Code"
                          >
                            <CopyIcon fontSize="small" />
                          </IconButton>
                        )}
                      </Box>
                    </Box>

                    {res.reasoning && (
                      <Box sx={{ mb: 3 }}>
                        <Typography
                          variant="caption"
                          color="text.secondary"
                          fontWeight={600}
                          sx={{ letterSpacing: 0.5 }}
                        >
                          REASONING & ANALYSIS
                        </Typography>
                        <Paper
                          elevation={0}
                          sx={{
                            p: 2,
                            bgcolor: 'primary.50',
                            mt: 1,
                            borderRadius: 1.5,
                            border: '1px solid',
                            borderColor: 'primary.100',
                          }}
                        >
                          <Typography
                            variant="body2"
                            sx={{ whiteSpace: 'pre-wrap', lineHeight: 1.7 }}
                          >
                            {res.reasoning}
                          </Typography>
                        </Paper>
                      </Box>
                    )}
                  </Paper>
                ))}
              </Box>
            )}
          </TabPanel>

          {/* Excel Add-in Tab */}
          <TabPanel value={activeTab} index={2}>
            <ExcelAddinTab />
          </TabPanel>

          {/* History Tab */}
          <TabPanel value={activeTab} index={3}>
            <TransactionHistory
              transactions={history}
              loading={historyLoading}
              onRefresh={loadHistory}
              onViewDetails={handleViewDetails}
              onDownload={handleDownloadFromHistory}
              onDownloadBatch={handleDownloadBatchFromHistory}
              onDownloadMergedBatch={handleDownloadMergedBatch}
              onResumeMapping={handleResumeMapping}
              resumingMappingId={resumingMappingId}
            />
          </TabPanel>
        </Paper>

        <Alert
          severity="info"
          sx={{
            mt: 2,
            bgcolor: brand.sidebarActiveBg,
            border: `1px solid ${brand.orange}40`,
            color: brand.textPrimary,
            '& .MuiAlert-icon': { color: brand.orange },
          }}
        >
          <Typography variant="body2" fontWeight={600} gutterBottom>
            {t('dashboard.cards.tariffClassifier.howItWorks.title')}
          </Typography>
          <Typography variant="caption" component="div">
            • {t('dashboard.cards.tariffClassifier.howItWorks.direct')}
          </Typography>
          <Typography variant="caption" component="div">
            • {t('dashboard.cards.tariffClassifier.howItWorks.file')}
          </Typography>
          <Typography variant="caption" component="div">
            • {t('dashboard.cards.tariffClassifier.howItWorks.history')}
          </Typography>
        </Alert>
      </Container>

      <TariffTransactionDetailModal
        open={detailModalOpen}
        onClose={() => setDetailModalOpen(false)}
        transactionId={selectedTransactionId}
      />

      {/* Column Mapping Modal — opened once per bulk-uploaded file that needs mapping */}
      <ColumnMappingModal
        open={columnMappingOpen}
        onClose={() => {
          setColumnMappingOpen(false);
          setFilePreviewData(null);
          // The mapping queue is preserved so the user can resume via "Resolve mappings".
        }}
        previewData={filePreviewData}
        onConfirm={
          mappingContext === 'history' ? handleResumeMappingConfirm : handleBulkMappingConfirm
        }
        loading={mappingLoading}
      />
    </Box>
  );
};

export default TariffClassifierPage;
